import { BaseService } from './base.service';
import { CustomResponseEntity, PaginationParams } from '../types/common';
import { TrainingContentDto, CreateTrainingContentDto, UpdateTrainingContentDto } from '../types/dtos/training';
import { api } from '../client/axiosClient';

/**
 * TrainingContentService handles CRUD operations for training content.
 *
 * URL Pattern: /api/v1/trainings/{trainingId}/modules/{moduleId}/topics/{topicId}/contents
 * Organization context is provided via X-Organization-Id header (injected by axiosClient).
 *
 * Migration: The organizationId parameter has been removed from URL paths. The backend
 * extracts organization context from the X-Organization-Id header for tenant scoping.
 *
 * VAL-FE-006: Training Service Works with Header-Based Auth
 * The axiosClient automatically includes X-Organization-Id header from localStorage,
 * which the backend uses for tenant context validation via TenantContextHolder.
 *
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param trainingId - Parent training UUID for nested resource path
 * @param moduleId - Parent module UUID for nested resource path
 * @param topicId - Parent topic UUID for nested resource path
 */
export class TrainingContentService extends BaseService<
  TrainingContentDto,
  CreateTrainingContentDto,
  UpdateTrainingContentDto
> {
  /**
   * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
   * @param trainingId - Parent training UUID for nested resource path
   * @param moduleId - Parent module UUID for nested resource path
   * @param topicId - Parent topic UUID for nested resource path
   */
  constructor(
    _organizationId: string, 
    trainingId: string, 
    moduleId: string, 
    topicId: string
  ) {
    super(`/api/v1/trainings/${trainingId}/modules/${moduleId}/topics/${topicId}/contents`);
  }

  findAll(params?: PaginationParams): Promise<CustomResponseEntity<TrainingContentDto[]>> {
    return api.get(this.baseUrl, { params });
  }

  // Upload content with file (multipart/form-data).
  // Explicit header matches other upload services; the axios interceptor
  // lets the browser complete it with the multipart boundary.
  uploadWithFile(formData: FormData): Promise<CustomResponseEntity<TrainingContentDto>> {
    return api.post(`${this.baseUrl}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // Update existing content with file replacement (multipart/form-data).
  // Explicit header matches other upload services; the axios interceptor
  // lets the browser complete it with the multipart boundary.
  updateWithFile(id: string, formData: FormData): Promise<CustomResponseEntity<TrainingContentDto>> {
    return api.put(`${this.baseUrl}/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }
}

/**
 * Factory function to create a TrainingContentService instance
 * @param _organizationId - Deprecated: Organization ID (no longer used, kept for backward compatibility)
 * @param trainingId - Parent training UUID for nested resource path
 * @param moduleId - Parent module UUID for nested resource path
 * @param topicId - Parent topic UUID for nested resource path
 */
export const createTrainingContentService = (
  _organizationId: string, 
  trainingId: string, 
  moduleId: string,
  topicId: string
) => new TrainingContentService(_organizationId, trainingId, moduleId, topicId);

